import { useId, useRef, useState, type DragEvent } from 'react';
import { isAxiosError } from 'axios';
import { Download, Info, Loader2, Upload } from 'lucide-react';
import type { Lender } from '../../api/lenders';
import { useImportLenderUsers } from '../../hooks/useLenders';
import { downloadImportTemplate, importColumns, readImportFile, type ImportFileResult } from './lenderUsersImport';
import folderIcon from './assets/users-import-folder.svg';

const FILE_PROBLEMS = "We couldn't import this file. Fix these rows and upload it again:";
const IMPORT_FAILED = 'Unable to import the users. Please try again.';
const TEMPLATE_HINT = 'Use the template to keep your import organized and avoid errors.';
const TEMPLATE_FAILED = 'Unable to download the template. Please try again.';

const users = (count: number) => `${count} ${count === 1 ? 'user' : 'users'}`;

const sizeOf = (file: File) => `${Math.max(1, Math.round(file.size / 1024))} KB`;

const listed = (items: string[]) =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

interface ImportLenderUsersDialogProps {
  lender: Lender;
  onClose: () => void;
  onImported: (count: number) => void;
}

/** Import Users: reads the chosen file, lists what is wrong in it by row, and adds every user or none. */
export default function ImportLenderUsersDialog({ lender, onClose, onImported }: ImportLenderUsersDialogProps) {
  const importUsers = useImportLenderUsers();
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const readingRef = useRef(0);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportFileResult | null>(null);
  const [rejected, setRejected] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  const [templateFailed, setTemplateFailed] = useState(false);
  const [dragging, setDragging] = useState(false);

  const importing = importUsers.isPending;
  const problems = [...(result?.errors ?? []), ...rejected];
  const count = problems.length === 0 ? (result?.users.length ?? 0) : 0;
  const columns = listed(importColumns(lender).map((column) => column.header));

  const choose = async (chosen: File | undefined) => {
    if (!chosen) return;
    const reading = ++readingRef.current;
    setFile(chosen);
    setResult(null);
    setRejected([]);
    setFailed(false);
    const read = await readImportFile(chosen, lender);
    // A file chosen or removed meanwhile wins over this one.
    if (reading === readingRef.current) setResult(read);
  };

  const remove = () => {
    readingRef.current += 1;
    setFile(null);
    setResult(null);
    setRejected([]);
    setFailed(false);
  };

  // The upload area stays with a file chosen, so a file dropped on it then takes that one's place.
  const drop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (!importing) void choose(event.dataTransfer.files[0]);
  };

  const submit = async () => {
    if (!result || count === 0) return;
    setFailed(false);
    try {
      const { imported } = await importUsers.mutateAsync({
        urn: lender.urn,
        users: result.users.map((entry) => entry.user),
      });
      onImported(imported);
    } catch (error) {
      // The server checks what the file cannot: emails already used in the lender. It answers
      // the emails at fault under `data`, shown by the row they are in like any other problem.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const emails: unknown = isAxiosError(error) ? error.response?.data?.data?.emails : undefined;
      if ((status === 409 || status === 400) && Array.isArray(emails) && emails.length > 0) {
        setRejected(
          emails.map((email: string) => {
            const row = result.users.find((entry) => entry.user.email.toLowerCase() === email.toLowerCase())?.row;
            const problem =
              status === 409 ? `${email} is already a user of this lender` : `${email} appears more than once in the file`;
            return row ? `Row ${row}: ${problem}` : problem;
          }),
        );
      } else {
        setFailed(true);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
      <div className="absolute inset-0 bg-black/40" onClick={importing ? undefined : onClose} />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={importing}
        className="relative flex max-h-full w-full max-w-[652px] flex-col gap-[25px] rounded-[11px] border-4 border-brand bg-surface p-6 text-content shadow-lg sm:p-[50px]"
      >
        <div className="flex flex-col gap-3 text-center">
          <h2 id={titleId} className="text-[22px] font-medium">Import Users</h2>
          <p className="text-[16px]">Upload a CSV or Excel file with {columns} columns.</p>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          <div
            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={drop}
            className={`flex flex-col items-center gap-6 rounded-[11px] border border-dashed py-8 ${
              file ? 'px-[18px]' : 'px-8'
            } ${dragging ? 'border-brand bg-brand-tint' : 'border-border bg-surface-sunken'}`}
          >
            {file ? (
              <div className="flex w-full items-center justify-between gap-4 rounded-xl border border-border-subtle bg-surface p-3 shadow-[0_2px_2px_rgba(15,27,47,0.03)]">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{file.name}</p>
                  <p className="mt-0.5 text-xs text-content-secondary">{sizeOf(file)}</p>
                </div>
                <button
                  type="button"
                  onClick={remove}
                  disabled={importing}
                  className="text-xs font-semibold text-red-600 underline disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center gap-2">
                  <img src={folderIcon} alt="" className="h-[17.5px] w-5" />
                  <p className="text-cell text-content-secondary">Drag and drop or choose a file to upload</p>
                </div>
                <div className="flex w-full flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      setTemplateFailed(false);
                      downloadImportTemplate(lender).catch(() => setTemplateFailed(true));
                    }}
                    className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 text-base font-semibold text-content-secondary"
                  >
                    <Download className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
                    Download template
                  </button>
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-brand bg-surface px-4 text-base font-semibold text-brand"
                  >
                    <Upload className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
                    Choose file
                  </button>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.csv"
                  aria-label="Choose file"
                  className="sr-only"
                  tabIndex={-1}
                  onChange={(event) => {
                    void choose(event.target.files?.[0]);
                    // The same file can be chosen again after fixing it.
                    event.target.value = '';
                  }}
                />
                <p className="flex items-center justify-center gap-2 text-center text-cell text-content-secondary">
                  <Info className="h-4 w-4 shrink-0" strokeWidth={3} aria-hidden="true" />
                  {TEMPLATE_HINT}
                </p>
              </>
            )}
          </div>

          {problems.length > 0 && (
            <div role="alert" className="text-sm text-red-600">
              <p>{FILE_PROBLEMS}</p>
              <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto pl-4">
                {problems.map((problem) => <li key={problem}>{problem}</li>)}
              </ul>
            </div>
          )}
          {failed && <p role="alert" className="text-sm text-red-600">{IMPORT_FAILED}</p>}
          {templateFailed && <p role="alert" className="text-sm text-red-600">{TEMPLATE_FAILED}</p>}
        </div>

        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="h-11 rounded-lg border border-brand bg-surface px-[50px] text-base font-semibold text-content disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={count === 0 || importing}
            className="inline-flex h-11 min-w-[151px] items-center justify-center gap-2 rounded-lg bg-brand px-4 text-base font-semibold text-white disabled:opacity-20"
          >
            {importing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {importing ? `Importing ${users(count)}…` : count > 0 ? `Import ${count} ${count === 1 ? 'User' : 'Users'}` : 'Import Users'}
          </button>
        </div>
      </section>
    </div>
  );
}
